import { Button } from "@/components/ui/button";
import { Heart, Instagram, Twitter, Facebook, Linkedin, Youtube, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import friendatoLogo from "@/assets/friendato-logo.png";

const Footer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const socialLinks = [
    { icon: Instagram, href: "https://www.instagram.com/friendato1/", label: "Instagram" },
    { icon: Twitter, href: "https://twitter.com/friendato", label: "Twitter" },
    { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61584912038685", label: "Facebook" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/110600516", label: "LinkedIn" },
    { icon: Youtube, href: "https://youtube.com/@friendato", label: "YouTube" },
  ];

  return (
    <>
      {/* Footer CTA */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-hero bg-gradient-animated" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,transparent_0%,hsl(var(--background)/0.3)_100%)]" />
        
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary-foreground/20 to-transparent" />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto stagger-children">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary-foreground/10 backdrop-blur-xl mb-8 border border-primary-foreground/20">
              <Heart className="w-10 h-10 text-primary-foreground" />
            </div>
            
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6 tracking-tight">
              {t('footer.ctaTitle')}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
              {t('footer.ctaSubtitle')}
            </p>
            <Button 
              size="lg"
              className="group bg-primary-foreground text-primary hover:bg-primary-foreground/90 px-10 py-7 text-lg rounded-2xl shadow-elevated hover:shadow-glow hover:scale-[1.02] transition-all duration-300 font-semibold"
              onClick={() => navigate('/matches')}
            >
              {t('footer.ctaButton')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-card border-t border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 mb-12">
            {/* Company */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-6 uppercase tracking-wider">{t('footer.company')}</h3>
              <ul className="space-y-4">
                <li><Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.blog')}</Link></li>
                <li><Link to="/careers" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.careers')}</Link></li>
                <li><Link to="#" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.aboutUs')}</Link></li>
              </ul>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-6 uppercase tracking-wider">{t('footer.product')}</h3>
              <ul className="space-y-4">
                <li><Link to="/matches" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.signUp')}</Link></li>
                <li><Link to="#" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.premium')}</Link></li>
                <li><Link to="/success-stories" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.successStories')}</Link></li>
              </ul>
            </div>

            {/* Help & Support */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-6 uppercase tracking-wider">{t('footer.support')}</h3>
              <ul className="space-y-4">
              <li><Link to="/safety" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.safety')}</Link></li>
                <li><Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.faq')}</Link></li>
                <li><Link to="/behavior" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.community')}</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-bold text-foreground mb-6 uppercase tracking-wider">{t('footer.legal')}</h3>
              <ul className="space-y-4">
                <li><Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.terms')}</Link></li>
                <li><Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.privacy')}</Link></li>
              </ul>
            </div>
          </div>

          {/* Social Media */}
          <div className="flex justify-center gap-3 mb-10">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:scale-110"
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img 
                src={friendatoLogo} 
                alt="Friendato" 
                className="w-10 h-10 rounded-xl object-cover"
              />
              <span className="font-bold text-xl bg-gradient-accent bg-clip-text text-transparent">Friendato</span>
            </div>
            <p className="text-sm text-muted-foreground">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
